// Phaser/functions/sound.ts
import { getEngine } from "./engineManager";

/** Normalise l'identifiant du son en string */
const asId = (id: string | number) => String(id);

/** Essaie de récupérer l'instance Sound par différents chemins connus */
function resolveSound(name: string) {
  const engine = getEngine();
  if (!engine) return { engine: undefined, scene: undefined, sound: undefined as any };

  const scene = engine.phaserScene as any;

  // 1) Méthode de scène (la plus directe)
  const byScene = scene?.getSoundByName?.(name);
  if (byScene) return { engine, scene, sound: byScene };

  // 2) Méthode d'engine si dispo
  const byEngine = (engine as any).getSound?.(name);
  if (byEngine) return { engine, scene, sound: byEngine };

  // 3) AudioManager (accès indirect)
  const am = (engine as any).getAudioManager?.();
  const byAM = am?.getByName?.(name);
  if (byAM) return { engine, scene, sound: byAM };

  return { engine, scene, sound: undefined };
}

/** Joue le son dont le **nom** est fourni. */
export const playSound = (name: string | number): void => {
  const id = asId(name);
  const { engine, scene, sound } = resolveSound(id);

  if (!engine || !scene) {
    console.warn("playSound(): engine ou scene introuvable.");
    return;
  }

  if (sound) {
    // Si déjà en lecture, on redémarre pour éviter la superposition
    try { sound.stop?.(); } catch { }
    sound.play?.();
    return;
  }

  // Pas trouvé : tentative de lazy-load si l'API existe
  if (scene.ensureSound) {
    scene.ensureSound(id, () => {
      const s2 = scene.getSoundByName?.(id);
      if (!s2) return console.warn(`playSound(): sound "${id}" not found after ensureSound.`);
      try { s2.stop?.(); } catch { }
      s2.play?.();
    });
    return;
  }

  // Dernier fallback : AudioManager en “fire-and-forget”
  const am = (engine as any).getAudioManager?.();
  if (am?.playByName) {
    am.playByName(id);
    return;
  }

  console.warn(`playSound(): sound "${id}" not found.`);
};

/** Arrête ce son. */
export const stopSound = (name: string | number): void => {
  const id = asId(name);
  const { engine, scene, sound } = resolveSound(id);

  if (!engine || !scene) {
    console.warn("stopSound(): engine ou scene introuvable.");
    return;
  }

  if (sound) {
    sound.stop?.();
    return;
  }

  // Fallback AudioManager
  const am = (engine as any).getAudioManager?.();
  if (am?.stopByName) {
    am.stopByName(id);
    return;
  }

  console.warn(`stopSound(): sound "${id}" not found.`);
};
